import React, { useEffect, useState } from 'react';
import { motion } from 'framer-motion';
import { Link } from 'react-router-dom';
import Confetti from 'react-confetti';
import SafeIcon from '../common/SafeIcon';
import { LOGOS } from '../assets/logos';
import * as FiIcons from 'react-icons/fi';

const { FiCheck, FiMail, FiClock, FiHeart } = FiIcons;

const ThankYouPage = () => {
  const [showConfetti, setShowConfetti] = useState(true);
  const [windowDimensions, setWindowDimensions] = useState({
    width: window.innerWidth,
    height: window.innerHeight,
  });

  useEffect(() => {
    const timer = setTimeout(() => setShowConfetti(false), 5000);
    const handleResize = () => {
      setWindowDimensions({
        width: window.innerWidth,
        height: window.innerHeight,
      });
    };

    window.addEventListener('resize', handleResize);
    return () => {
      clearTimeout(timer);
      window.removeEventListener('resize', handleResize);
    };
  }, []);

  return (
    <div className="min-h-screen bg-gradient-to-br from-green-50 to-blue-50 flex items-center justify-center p-6">
      {showConfetti && (
        <Confetti
          width={windowDimensions.width}
          height={windowDimensions.height}
          recycle={false}
          numberOfPieces={200}
          gravity={0.3}
        />
      )}

      <motion.div
        initial={{ opacity: 0, y: 30, scale: 0.9 }}
        animate={{ opacity: 1, y: 0, scale: 1 }}
        transition={{ duration: 0.6, ease: "easeOut" }}
        className="max-w-2xl w-full bg-white rounded-3xl shadow-2xl p-8 text-center"
      >
        {/* Success Icon */}
        <motion.div
          initial={{ scale: 0 }}
          animate={{ scale: 1 }}
          transition={{ delay: 0.3, type: "spring", stiffness: 200 }}
          className="w-24 h-24 bg-green-500 rounded-full flex items-center justify-center mx-auto mb-6"
        >
          <SafeIcon icon={FiCheck} className="w-12 h-12 text-white" />
        </motion.div>

        {/* Logo VIXEA */}
        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={{ delay: 0.4 }}
          className="mb-6"
        >
          <img src={LOGOS.BLUE_TRANSPARENT} alt="VIXEA" className="h-16 mx-auto" />
        </motion.div>

        {/* Main Message */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.5 }}
          className="mb-8"
        >
          <h1 className="text-4xl font-bold text-gray-900 mb-4">
            ¡Entrevista Completada!
          </h1>
          <p className="text-xl text-gray-600 mb-6">
            Gracias por tu tiempo y dedicación. Hemos recibido todas tus respuestas exitosamente.
          </p>
        </motion.div>

        {/* Info Cards */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.7 }}
          className="grid md:grid-cols-2 gap-6 mb-8"
        >
          <div className="bg-blue-50 p-6 rounded-xl">
            <div className="w-12 h-12 bg-blue-500 rounded-lg flex items-center justify-center mx-auto mb-4">
              <SafeIcon icon={FiMail} className="w-6 h-6 text-white" />
            </div>
            <h3 className="text-lg font-semibold text-gray-900 mb-2">
              Te contactaremos pronto
            </h3>
            <p className="text-gray-600 text-sm">
              Nuestro equipo de recursos humanos revisará tu entrevista y se pondrá en contacto contigo en los próximos días.
            </p>
          </div>
          <div className="bg-orange-50 p-6 rounded-xl">
            <div className="w-12 h-12 bg-orange-500 rounded-lg flex items-center justify-center mx-auto mb-4">
              <SafeIcon icon={FiClock} className="w-6 h-6 text-white" />
            </div>
            <h3 className="text-lg font-semibold text-gray-900 mb-2">
              Videos disponibles por 15 días
            </h3>
            <p className="text-gray-600 text-sm">
              Tus respuestas estarán disponibles para revisión durante 15 días, después serán eliminadas automáticamente.
            </p>
          </div>
        </motion.div>

        {/* Additional Message */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.9 }}
          className="bg-gradient-to-r from-primary to-blue-600 p-6 rounded-xl text-white mb-8"
        >
          <div className="flex items-center justify-center space-x-2 mb-3">
            <SafeIcon icon={FiHeart} className="w-5 h-5 text-red-300" />
            <span className="font-semibold">Mensaje del equipo</span>
          </div>
          <p className="text-blue-100">
            Nos encanta conocer a personas talentosas como tú. Sin importar el resultado, valoramos tu interés en formar parte de nuestro equipo. ¡Esperamos poder trabajar juntos pronto!
          </p>
        </motion.div>

        {/* Dark mode version */}
        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={{ delay: 1.1 }}
          className="bg-gray-900 p-6 rounded-xl text-white mb-8"
        >
          <div className="flex items-center justify-center space-x-3 mb-4">
            <img src={LOGOS.WHITE_TRANSPARENT} alt="VIXEA" className="h-8 object-contain" />
          </div>
          <p className="text-gray-300 text-sm">
            Entrevista realizada con la tecnología de VIXEA - Entrevistas Inteligentes
          </p>
        </motion.div>

        {/* Footer */}
        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={{ delay: 1.3 }}
          className="mt-8 pt-6 border-t border-gray-200"
        >
          <p className="text-sm text-gray-500 mb-4">
            ¿Tienes alguna pregunta sobre el proceso? Puedes contactar al equipo de recursos humanos.
          </p>
          <Link
            to="/"
            className="inline-block px-6 py-2 text-primary hover:text-blue-700 transition-colors font-medium"
          >
            Volver al inicio
          </Link>
        </motion.div>
      </motion.div>
    </div>
  );
};

export default ThankYouPage;